import { useState } from 'react';
import { Button } from 'react-bootstrap';
import { listPlatformCharges, decidePlatformCharge } from '../api/financeApi';
import useFinanceQuery from '../useFinanceQuery';
import FinanceListPage from '../components/FinanceListPage';
import DetailLinkButton from '../components/DetailLinkButton';
import MarkPaymentModal from '../components/MarkPaymentModal';
import ConfirmModal from '../components/ConfirmModal';
import MessageModal from '../components/MessageModal';
import { useFinance } from '../financeContext';
import { money } from '../format';
import { StatusBadge, OverdueBadge, remindState } from './rowBits';

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'escalated', label: 'Escalated' },
  { value: 'paid', label: 'Paid' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'waived', label: 'Waived' }
];

export default function VisitFeesPage() {
  const { canApproveFinance, isAdmin } = useFinance();
  const q = useFinanceQuery(
    (params) => listPlatformCharges({ status: 'pending', ...params }),
    { filterKeys: ['status', 'vertical', 'overdue', 'minAmount', 'maxAmount', 'dateFrom', 'dateTo', 'ownerId'] }
  );
  const [payTarget, setPayTarget] = useState(null);
  const [busyId, setBusyId] = useState('');
  const [actionError, setActionError] = useState('');
  const [confirm, setConfirm] = useState(null);

  const act = async (id, action) => {
    setBusyId(id);
    try {
      await decidePlatformCharge(id, { action });
      await q.reload();
    } catch (err) {
      setActionError(err?.response?.data?.msg || 'Unable to update that charge.');
    } finally {
      setBusyId('');
    }
  };

  const columns = [
    { key: 'ref', header: 'Visit', cardHeader: true, cell: (r) => <DetailLinkButton kind="visit" entity={{
      // Never spread the platform-charge row: its _id is the fee document, not the visit.
      // VisitDetailsModal loads GET /api/real_estate/visits/:id with this id.
      _id: r.bookingId || r.visitId,
      visitId: r.bookingId || r.visitId,
      bookingId: r.bookingId || r.visitId
    }} label={`#${r.bookingRef || (r.bookingId || '').slice(-6)}`} /> },
    { key: 'vertical', header: 'Vertical', cell: (r) => r.vertical },
    { key: 'owner', header: 'Owner', cell: (r) => (
      <span>
        {r.ownerId
          ? <DetailLinkButton kind="staff" id={r.ownerId} label={r.ownerName || 'owner'} />
          : (r.ownerName || '—')}
        <div className="small text-muted">{[r.ownerEmail, r.ownerPhone].filter(Boolean).join(' · ')}</div>
      </span>
    ) },
    { key: 'amount', header: 'Amount', cell: (r) => money(r.amount) },
    { key: 'status', header: 'Status', cell: (r) => <StatusBadge status={r.status} extra={<OverdueBadge row={r} />} /> }
  ];

  const rowActions = !canApproveFinance ? undefined : (r) => {
    if (!['pending', 'escalated'].includes(r.status)) {
      return <span className="small text-muted">{r.reviewerNote || '—'}</span>;
    }
    const rs = remindState(r);
    return (
      <div className="d-flex flex-wrap gap-1">
        {rs.show && (
          <Button size="sm" variant="outline-secondary" disabled={busyId === r._id} onClick={() => act(r._id, 'remind')}>
            Remind owner
          </Button>
        )}
        <Button size="sm" variant="success" onClick={() => setPayTarget({ kind: 'platformCharge', id: r._id, amount: r.amount, title: `#${r.bookingRef || ''}` })}>
          Mark paid
        </Button>
        <Button size="sm" variant="outline-danger" disabled={busyId === r._id} onClick={() => setConfirm({
          id: r._id, action: 'reject', title: 'Reject this visit fee?',
          message: 'The owner will not be charged. This cannot be undone from here.'
        })}>
          Reject
        </Button>
        <Button size="sm" variant="outline-secondary" disabled={busyId === r._id} onClick={() => setConfirm({
          id: r._id, action: 'waive', title: 'Waive this visit fee?',
          message: 'The fee is written off. The owner will not owe this amount.'
        })}>
          Waive
        </Button>
        {rs.hint && <div className="w-100 small text-muted">{rs.hint}</div>}
      </div>
    );
  };

  return (
    <>
      <FinanceListPage
        title="Property visit fees"
        subtitle="Platform's per-confirmed-visit fee that a real-estate owner owes."
        q={q}
        columns={columns}
        rowActions={rowActions}
        filterFields={['status', 'vertical', 'overdue', 'amount', 'date', 'owner']}
        statusOptions={STATUS_OPTIONS}
        searchPlaceholder="Search booking ref, owner name or email"
        emptyText="No visit fees in this state."
      />
      <MarkPaymentModal
        target={payTarget}
        show={!!payTarget}
        asAdmin={isAdmin}
        onHide={() => setPayTarget(null)}
        onRecorded={() => { setPayTarget(null); q.reload(); }}
      />
      <ConfirmModal
        show={!!confirm}
        onHide={() => setConfirm(null)}
        title={confirm?.title}
        message={confirm?.message}
        confirmLabel={confirm?.action === 'waive' ? 'Waive' : 'Reject'}
        confirmVariant="danger"
        busy={busyId === confirm?.id}
        onConfirm={async () => {
          if (!confirm) return;
          await act(confirm.id, confirm.action);
          setConfirm(null);
        }}
      />
      <MessageModal
        show={!!actionError}
        onHide={() => setActionError('')}
        title="Could not update charge"
        message={actionError}
      />
    </>
  );
}
